export function normalizeBelgianPhoneNumber(phone: string): string {
  if (!phone) return '';

  // Remove all non-digit characters to work with a clean slate.
  const digits = phone.replace(/\D/g, '');

  // The core of a Belgian mobile number is a 9-digit sequence starting with '4'.
  // We find the beginning of this sequence.
  const mobilePartIndex = digits.indexOf('4');

  // If '4' is not found, it's not a recognizable Belgian mobile format.
  if (mobilePartIndex === -1) {
    return phone; // Return original to avoid breaking other number types.
  }

  // Extract the potential 9-digit mobile number part.
  const nationalPart = digits.substring(mobilePartIndex);

  // If it has the correct length, we've found our number.
  if (nationalPart.length === 9) {
    return `+32${nationalPart}`;
  }

  // If the length is incorrect, it's an unknown or invalid format.
  return phone;
}

/**
 * Normalizes phone numbers for Belgium, Netherlands, and France
 * Returns E164 format (+XX...) or original if not recognizable
 * Handles spaces, leading zeros, and various formats
 */
export function normalizePhoneNumber(phone: string): string {
  if (!phone) return '';

  // Remove all spaces and non-digit characters except +
  const cleaned = phone.replace(/\s+/g, '').trim();

  // If already in E164 format (+XX...), validate and return
  if (cleaned.startsWith('+')) {
    const e164Digits = cleaned.replace(/\D/g, '');
    // Belgium: +32 + 9 digits = 11 total
    if (e164Digits.startsWith('32') && e164Digits.length === 11) {
      return `+${e164Digits}`;
    }
    // Netherlands: +31 + 9 digits = 11 total
    if (e164Digits.startsWith('31') && e164Digits.length === 11) {
      return `+${e164Digits}`;
    }
    // France: +33 + 9 digits = 11 total
    if (e164Digits.startsWith('33') && e164Digits.length === 11) {
      return `+${e164Digits}`;
    }
    // If it's already E164 but not recognized, return as-is
    return cleaned;
  }

  // Handle international format with 00 prefix
  if (cleaned.startsWith('00')) {
    const digits = cleaned.replace(/\D/g, '');
    // Belgium: 0032 + 9 digits = 13 total
    if (digits.startsWith('0032') && digits.length === 13) {
      return `+32${digits.slice(4)}`;
    }
    // Netherlands: 0031 + 9 digits = 13 total
    if (digits.startsWith('0031') && digits.length === 13) {
      return `+31${digits.slice(4)}`;
    }
    // France: 0033 + 9 digits = 13 total
    if (digits.startsWith('0033') && digits.length === 13) {
      return `+33${digits.slice(4)}`;
    }
  }

  // Remove all non-digit characters to work with a clean slate
  const digits = cleaned.replace(/\D/g, '');

  // Handle leading zero (national format) - remove it
  // Belgium: 0491748175 -> 491748175 -> +32491748175
  // Netherlands: 0612345678 -> 612345678 -> +31612345678
  // France: 0612345678 -> 612345678 -> +33612345678
  let workingDigits = digits;
  if (digits.startsWith('0') && digits.length === 10) {
    workingDigits = digits.slice(1); // Remove leading zero
  }

  // Country detection patterns for mobile numbers (9 digits total after country code)
  const patterns = [
    { countryCode: '+32', mobilePrefixes: ['4'], description: 'Belgium' },
    { countryCode: '+31', mobilePrefixes: ['6'], description: 'Netherlands' },
    { countryCode: '+33', mobilePrefixes: ['6', '7'], description: 'France' },
  ];

  // First, try to match as national number (9 digits starting with mobile prefix)
  for (const pattern of patterns) {
    for (const prefix of pattern.mobilePrefixes) {
      // Look for the prefix at the beginning of a potential national number
      // This handles cases like "491748175" (BE), "612345678" (NL/FR), "712345678" (FR)
      if (workingDigits.startsWith(prefix) && workingDigits.length === 9) {
        return `${pattern.countryCode}${workingDigits}`;
      }
    }
      }

  // Then, try to match as international format without + but with country code
      // Like "32491748175" (BE), "31612345678" (NL), "33612345678" (FR), "33712345678" (FR)
  for (const pattern of patterns) {
      const countryDigits = pattern.countryCode.slice(1); // Remove '+'
    for (const prefix of pattern.mobilePrefixes) {
      if (digits.startsWith(`${countryDigits}${prefix}`) && digits.length === countryDigits.length + 9) {
        return `${pattern.countryCode}${digits.slice(countryDigits.length)}`;
      }
    }
  }

  // If no pattern matches, return original to avoid breaking other number types
  return phone;
}

/**
 * Generates a unique dummy phone number for import
 * Format: +32 followed by 9 digits starting with 4 (Belgian mobile format)
 * Uses timestamp and random component for uniqueness
 */
export function generateDummyPhoneNumber(index: number): string {
  // Use index and timestamp to ensure uniqueness
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 1000);
  const uniquePart = String(timestamp).slice(-6) + String(index).padStart(2, '0') + String(random).padStart(3, '0');
  // Take last 9 digits and ensure it starts with 4 (Belgian mobile prefix)
  const mobilePart = '4' + uniquePart.slice(-8);
  return `+32${mobilePart}`;
} 